import { Component, Input, Output, EventEmitter, ElementRef, OnChanges, SimpleChanges, OnDestroy, Renderer2, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ResumeAnalysisResult, ResumeAnalysisHelpers } from '../../services/resume-job-analyzer.service';
import { Job } from '../../types/job.types';
import { HugeiconsIconComponent } from '@hugeicons/angular';
import { Cancel01Icon, InformationCircleIcon, Upload01Icon } from '@hugeicons/core-free-icons';

@Component({
  selector: 'app-resume-results-modal',
  standalone: true,
  imports: [CommonModule, HugeiconsIconComponent],
  templateUrl: './resume-results-modal.component.html',
  styles: [`
    :host {
      display: none;
    }
  `]
})
export class ResumeResultsModalComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;

  icons = {
    cancel: Cancel01Icon,
    information: InformationCircleIcon,
    upload: Upload01Icon,
  };

  private portalElement: HTMLElement | null = null;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isOpen']) {
      if (changes['isOpen'].currentValue) {
        this.moveToBody();
      } else {
        this.removeFromBody();
      }
    }
  }

  ngOnDestroy() {
    this.removeFromBody();
  }

  private moveToBody() {
    if (!isPlatformBrowser(this.platformId) || this.portalElement) return;

    // Create a portal container
    this.portalElement = this.renderer.createElement('div');
    this.renderer.setStyle(this.portalElement, 'position', 'fixed');
    this.renderer.setStyle(this.portalElement, 'top', '0');
    this.renderer.setStyle(this.portalElement, 'left', '0');
    this.renderer.setStyle(this.portalElement, 'width', '100%');
    this.renderer.setStyle(this.portalElement, 'height', '100%');
    this.renderer.setStyle(this.portalElement, 'z-index', '10000');
    this.renderer.setStyle(this.portalElement, 'pointer-events', 'auto');

    // Move the component's content to the portal
    const hostElement = this.elementRef.nativeElement;
    const content = hostElement.firstChild;
    if (content) {
      this.renderer.appendChild(this.portalElement, content);
    }

    // Append to body
    this.renderer.appendChild(document.body, this.portalElement);
  }

  private removeFromBody() {
    if (this.portalElement && isPlatformBrowser(this.platformId)) {
      // Move content back to host
      const hostElement = this.elementRef.nativeElement;
      while (this.portalElement.firstChild) {
        this.renderer.appendChild(hostElement, this.portalElement.firstChild);
      }

      // Remove portal from body
      this.renderer.removeChild(document.body, this.portalElement);
      this.portalElement = null;
    } else if (this.portalElement) {
      this.portalElement = null;
    }
  }
  @Input() result!: ResumeAnalysisResult;
  @Input() job!: Job;
  @Output() close = new EventEmitter<void>();
  @Output() newAnalysis = new EventEmitter<void>();

  showAllDoingWell = false;
  showAllTips = false;

  closeModal() {
    this.close.emit();
  }

  runNewAnalysis() {
    this.newAnalysis.emit();
  }

  applyToJob() {
    if (isPlatformBrowser(this.platformId) && this.job?.application_url) {
      window.open(this.job.application_url, '_blank');
    }
  }

  scrollToActionRequired() {
    if (!isPlatformBrowser(this.platformId)) return;
    const element = document.getElementById('action-required-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  toggleDoingWell() {
    this.showAllDoingWell = !this.showAllDoingWell;
  }

  toggleTips() {
    this.showAllTips = !this.showAllTips;
  }

  getScoreLabel(score: number): { label: string; emoji: string } {
    return ResumeAnalysisHelpers.getScoreLabel(score);
  }

  parseHighlights(text: string): Array<{ text: string; highlight: boolean }> {
    return ResumeAnalysisHelpers.parseHighlights(text);
  }

  getVisibleDoingWell(): string[] {
    if (!this.result.doingWell) return [];
    return this.showAllDoingWell 
      ? this.result.doingWell.slice(0, 8)
      : this.result.doingWell.slice(0, 3);
  }

  getVisibleTips() {
    if (!this.result.additionalTips) return [];
    return this.showAllTips
      ? this.result.additionalTips.slice(0, 6)
      : this.result.additionalTips.slice(0, 2);
  }

  getExperienceMatchText(): string {
    const stats = this.result.stats;
    if (stats?.experiencesMatched !== undefined && stats?.experiencesTotal !== undefined) {
      return `${stats.experiencesMatched} of ${stats.experiencesTotal} requirements`;
    }
    // Fallback: try to estimate from percentage if available
    const percent = stats?.experiencesMatchPercent || this.result.experienceMatchScore;
    if (percent !== undefined) {
      // Estimate based on common job requirement counts (usually 3-5)
      const estimated = Math.round((percent / 100) * 4);
      return `${estimated} of 4 requirements`;
    }
    return '0 of 0 requirements';
  }

  getSkillsMatchText(): string {
    const stats = this.result.stats;
    if (stats?.skillsMatched !== undefined && stats?.skillsTotal !== undefined) {
      return `${stats.skillsMatched} of ${stats.skillsTotal} skills found`;
    }
    // Fallback: try to estimate from percentage if available
    const percent = stats?.skillsMatchPercent || this.result.skillsMatchScore;
    if (percent !== undefined) {
      // Estimate based on common skill counts (usually 6-10)
      const estimated = Math.round((percent / 100) * 8);
      return `${estimated} of 8 skills found`;
    }
    return '0 of 0 skills found';
  }

  getScoreColor(score: number): string {
    return ResumeAnalysisHelpers.getScoreTextColorClass(score);
  }

  // Gauge configuration - matches the SVG viewBox and arc paths
  private gaugeConfig = {
    centerX: 141.5,  // Center X of the arc
    centerY: 174,    // Base Y (bottom of arc)
    radius: 140,     // Radius of the arc
  };

  // Calculate the dot position based on score (0-100%)
  // The arc goes from left (0%) to right (100%) in a semi-circle
  getDotPosition(score: number): { x: number; y: number } {
    const { centerX, centerY, radius } = this.gaugeConfig;
    // Map score: 0% = 180° (left), 100% = 0° (right)
    const angle = 180 - (score / 100) * 180;
    const angleRad = (angle * Math.PI) / 180;
    
    return {
      x: centerX + radius * Math.cos(angleRad),
      y: centerY - radius * Math.sin(angleRad)
    };
  }

  // Get segment opacity based on score
  // Only segments that are FULLY completed (score >= segment end) are full opacity
  // The current segment and future segments are faded
  getSegmentOpacity(score: number, segmentEnd: number): number {
    return score >= segmentEnd ? 1 : 0.3;
  }

  // Get the dot color based on score range
  getDotColor(score: number): string {
    if (score >= 75) return '#8DBE00'; // Green
    if (score >= 50) return '#E5C500'; // Yellow
    if (score >= 25) return '#FA941A'; // Orange
    return '#CF5069'; // Red
  }

  /**
   * Generates an SVG clip path that covers from 0% (left) to the given score.
   * Creates a wedge shape from the center that clips the arc segments.
   */
  getScoreClipPath(score: number): string {
    const { centerX, centerY, radius } = this.gaugeConfig;
    // Larger radius to ensure the clip covers the entire arc width
    const clipRadius = radius + 50;
    
    // Start angle is 180° (left side, 0%)
    const startAngle = 180;
    // End angle based on score: 0% = 180°, 100% = 0°
    const endAngle = 180 - (score / 100) * 180;
    
    // Convert to radians
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;
    
    // Calculate start and end points on the arc
    const startX = centerX + clipRadius * Math.cos(startRad);
    const startY = centerY - clipRadius * Math.sin(startRad);
    const endX = centerX + clipRadius * Math.cos(endRad);
    const endY = centerY - clipRadius * Math.sin(endRad);
    
    // Large arc flag: 1 if angle > 180°, 0 otherwise
    const largeArcFlag = score > 50 ? 1 : 0;
    
    // Create a wedge path: center -> start -> arc to end -> back to center
    // M = move to center
    // L = line to start point
    // A = arc to end point
    // Z = close path back to center
    return `M ${centerX} ${centerY} L ${startX} ${startY} A ${clipRadius} ${clipRadius} 0 ${largeArcFlag} 1 ${endX} ${endY} Z`;
  }

  getDoingWellImage(): string {
    const isDark = isPlatformBrowser(this.platformId) &&
      document.documentElement.classList.contains('dark');
    return isDark
      ? 'assets/svgs/doing-well-dark.svg'
      : 'assets/svgs/doing-well.svg';
  }

  getTargetImage(): string {
    const isDark = isPlatformBrowser(this.platformId) &&
      document.documentElement.classList.contains('dark');
    return isDark
      ? 'assets/images/target-dark.svg'
      : 'assets/images/target.svg';
  }
}
